import React, { useMemo, useState } from "react";
import { ATMGrid } from "components/atoms/ATMGrid/ATMGrid.component";
import { ATMButton } from "components/atoms/ATMButton/ATMButton.component";
import { ATMModal } from "components/atoms/ATMModal/ATMModal.component";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import { Box, Chip } from "@mui/material";
import { ATMLoader } from "components/atoms/ATMLoader/ATMLoader.component";
import useUpdateFumigationCompany from "hooks/useUpdateFumigationCompany";
import useSnackbar from "hooks/useSnackbar";
import { FumigationCompanyDetail } from "services/fumigationCompany.types";
import moment from "moment";
import styles from "./PendingReview.module.scss";

interface PendingReviewProps {
    /** Full record list passed down from AdminPortalPage (already cached). */
    allRecords: FumigationCompanyDetail[];
    isLoading: boolean;
    /** Invalidates the shared query cache after a mutation so all tabs refresh. */
    onRefresh: () => void;
}

const PendingReview: React.FC<PendingReviewProps> = ({ allRecords, isLoading, onRefresh }) => {
    const { showSnackbar, snackbar } = useSnackbar();
    const { mutate: updateFumigationCompany, isLoading: isUpdating } = useUpdateFumigationCompany();
    const [rejectTarget, setRejectTarget] = useState<{ name: string; action: 'reject' | 'delete' } | null>(null);
    const [activeAction, setActiveAction] = useState<{ companyName: string; action: 'approve' | 'reject' | 'delete' } | null>(null);

    // Derive pending rows from the shared cached list — no extra fetch needed.
    const rows = useMemo(
        () => allRecords.filter(item => (item.status ?? '').toUpperCase() === 'PENDING'),
        [allRecords]
    );

    const handleApprove = (fumCompanyNm: string) => {
        setActiveAction({ companyName: fumCompanyNm, action: 'approve' });
        updateFumigationCompany(
            { fumCompanyNm, approved: true },
            {
                onSuccess: () => {
                    showSnackbar('Fumigation company approved.', 'success');
                    onRefresh();
                },
                onError: (error) => {
                    console.error('Error approving fumigation company:', error);
                    showSnackbar('Failed to approve fumigation company.', 'error');
                },
                onSettled: () => {
                    setActiveAction(null);
                },
            }
        );
    };

    const handleRejectClick = (fumCompanyNm: string) => {
        setRejectTarget({ name: fumCompanyNm, action: 'reject' });
    };

    const handleDeleteClick = (fumCompanyNm: string) => {
        setRejectTarget({ name: fumCompanyNm, action: 'delete' });
    };

    const confirmRejectOrDelete = () => {
        if (!rejectTarget) return;

        const { name, action } = rejectTarget;
        setActiveAction({ companyName: name, action });
        
        if (action === 'reject') {
            // Set status to REJECTED
            updateFumigationCompany(
                { fumCompanyNm: name, rejected: true },
                {
                    onSuccess: () => {
                        showSnackbar('Fumigation company rejected.', 'success');
                        onRefresh();
                    },
                    onError: (error) => {
                        console.error('Error rejecting fumigation company:', error);
                        showSnackbar('Failed to reject fumigation company.', 'error');
                    },
                    onSettled: () => {
                        setRejectTarget(null);
                        setActiveAction(null);
                    },
                }
            );
        } else {
            // Soft delete (set status to DELETED)
            updateFumigationCompany(
                { fumCompanyNm: name, deleted: true },
                {
                    onSuccess: () => {
                        showSnackbar('Fumigation company deleted.', 'success');
                        onRefresh();
                    },
                    onError: (error) => {
                        console.error('Error deleting fumigation company:', error);
                        showSnackbar('Failed to delete fumigation company.', 'error');
                    },
                    onSettled: () => {
                        setRejectTarget(null);
                        setActiveAction(null);
                    },
                }
            );
        }
    };

    const columns: GridColDef[] = [
        { field: 'fumCompanyNm', headerName: 'Company Name', flex: 1, minWidth: 200 },
        { field: 'fumPhoneNbr', headerName: 'Phone Number', width: 160 },
        { field: 'fumNotes', headerName: 'Notes', flex: 2, minWidth: 250 },
        {
            field: 'updateDttm',
            headerName: 'Last Updated',
            width: 180,
            valueFormatter: (value: any) => {
                if (!value) return '';
                const parsed = moment(value);
                return parsed.isValid() ? parsed.format('MM/DD/YYYY hh:mm A') : value;
            },
        },
        {
            field: 'status',
            headerName: 'Status',
            width: 120,
            renderCell: (params) => {
                const status = params.value?.toUpperCase();
                let chipColor: 'success' | 'error' | 'warning' | 'default' | 'info' = 'default';
                let chipStyle: React.CSSProperties = {};
                
                switch (status) {
                    case 'ACTIVE':
                        chipColor = 'success';
                        chipStyle = { backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 500 };
                        break;
                    case 'REJECTED':
                        chipColor = 'error';
                        chipStyle = { backgroundColor: '#ffebee', color: '#c62828', fontWeight: 500 };
                        break;
                    case 'DELETED':
                        chipColor = 'default';
                        chipStyle = { backgroundColor: '#f5f5f5', color: '#616161', fontWeight: 500 };
                        break;
                    case 'PENDING':
                    default:
                        chipColor = 'info';
                        chipStyle = { backgroundColor: '#e3f2fd', color: '#1565c0', fontWeight: 500 };
                        break;
                }
                
                return <Chip label={params.value} color={chipColor} size="small" sx={chipStyle} />;
            },
        },
        {
            field: 'actions',
            headerName: 'Actions',
            width: 280,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const companyName = params.row.fumCompanyNm;
                const isThisRowActive = activeAction?.companyName === companyName;
                const isAnyActionActive = activeAction !== null;
                
                return (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', height: '100%' }}>
                        <ATMButton
                            content="Approve"
                            size="mini"
                            basic
                            loading={isThisRowActive && activeAction?.action === 'approve'}
                            disabled={isAnyActionActive}
                            onClick={() => handleApprove(companyName)}
                            style={{
                                color: '#2e7d32',
                                borderColor: '#2e7d32',
                            }}
                        />
                        <ATMButton
                            content="Reject"
                            size="mini"
                            basic
                            loading={isThisRowActive && activeAction?.action === 'reject'}
                            disabled={isAnyActionActive}
                            onClick={() => handleRejectClick(companyName)}
                            style={{
                                color: '#ed6c02',
                                borderColor: '#ed6c02',
                            }}
                        />
                        <ATMButton
                            content="Delete"
                            size="mini"
                            basic
                            loading={isThisRowActive && activeAction?.action === 'delete'}
                            disabled={isAnyActionActive}
                            onClick={() => handleDeleteClick(companyName)}
                            style={{
                                color: '#d32f2f',
                                borderColor: '#d32f2f',
                            }}
                        />
                    </div>
                );
            },
        },
    ];

    if (isLoading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <ATMLoader active inline="centered">Loading Pending Fumigation Companies...</ATMLoader>
            </Box>
        );
    }

    return (
        <ATMGrid className={styles.gridStyle}>
            {snackbar}

            {/* Reject/Delete Confirmation Modal */}
            <ATMModal
                open={!!rejectTarget}
                onClose={() => setRejectTarget(null)}
                size="tiny"
            >
                <ATMModal.Header>
                    {rejectTarget?.action === 'reject' ? 'Confirm Reject' : 'Confirm Delete'}
                </ATMModal.Header>
                <ATMModal.Content>
                    {rejectTarget?.action === 'reject' ? (
                        <>
                            Reject "{rejectTarget?.name}"? The company will be marked as REJECTED and can be reviewed later.
                        </>
                    ) : (
                        <>
                            Delete "{rejectTarget?.name}"? It will be moved to Deleted Fumigation Companies.
                        </>
                    )}
                </ATMModal.Content>
                <ATMModal.Actions>
                    <ATMButton
                        content="Cancel"
                        basic
                        disabled={isUpdating}
                        onClick={() => setRejectTarget(null)}
                    />
                    <ATMButton
                        content={rejectTarget?.action === 'reject' ? 'Reject' : 'Delete'}
                        negative={rejectTarget?.action === 'delete'}
                        color={rejectTarget?.action === 'reject' ? 'orange' : undefined}
                        loading={isUpdating}
                        disabled={isUpdating}
                        onClick={confirmRejectOrDelete}
                    />
                </ATMModal.Actions>
            </ATMModal>

            <ATMGrid.Row>
                <ATMGrid.Column width={16}>
                    <DataGrid
                        rows={rows}
                        columns={columns}
                        getRowId={(row) => row.fumCompanyNm}
                        initialState={{
                            pagination: { paginationModel: { page: 0, pageSize: 10 } },
                        }}
                        pageSizeOptions={[5, 10, 25]}
                        disableRowSelectionOnClick
                        disableColumnMenu={false}
                        sx={{
                            minHeight: 400,
                            '& .MuiDataGrid-columnHeaderTitle': { fontWeight: 'bold' },
                            '& .MuiDataGrid-columnHeader': { backgroundColor: '#f5f5f5' },
                            '& .MuiDataGrid-row:nth-of-type(even)': { backgroundColor: '#fafafa' },
                            '& .MuiDataGrid-row:nth-of-type(odd)': { backgroundColor: '#ffffff' },
                            '& .MuiDataGrid-row:hover': { backgroundColor: '#e3f2fd' },
                        }}
                    />
                </ATMGrid.Column>
            </ATMGrid.Row>
        </ATMGrid>
    );
};

export { PendingReview };
